<template>
  <div class="input-group-checkbox" :class="classes" :id="id">
    <label v-if="label || forReport || forState">
      <span v-if="label" v-html="label" />
      <Icon v-if="forReport" name="fieldsSAV" />
      <Icon v-if="forState" name="fieldsState" />
      <Tooltip v-if="tooltip" icon="info" :content="tooltip" />
    </label>
    <div>
      <Checkbox
        v-for="(option, idx) in computedOptions"
        :key="`input-group-checkbox-item-${idx}`"
        :value="option.value"
        :label="option.label"
        :checked.sync="ivalue"
        :vuelidate="vuelidate"
        :class="{ changed: ideleted.includes(option.value) }"
      />
      <button class="add" v-if="dynamic" @click="showAddValueModal">
        <span>+</span> {{ dynamicLabel }}
      </button>
    </div>
    <template v-if="reviewMode">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import Checkbox from '@/components/ui/inputs/Checkbox';
  import Icon from '@/components/ui/Icon';
  import Tooltip from '@/components/ui/Tooltip';
  import ModalAddValue from '@/components/modals/ModalAddValue';
  import VueUtil from '@/utils/VueUtil';

  let uid = 0;

  export default {
    name: 'CheckboxGroup',
    components: {
      Tooltip,
      Icon,
      Checkbox,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      label: {
        type: String,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      dynamic: {
        type: Boolean,
        default: false,
      },
      dynamicLabel: {
        type: String,
        default: null,
      },
      modal: {
        type: Object,
        default: () => ({
          title: '',
          description: '',
        }),
      },
      forceUnique: {
        type: [Boolean, Number, String],
        default: null,
      },
      options: {
        type: Array,
        default: null,
      },
      value: {
        type: [Array, Boolean, Number, String],
        default: () => [],
      },
      changes: {
        type: [Array, Object],
        default: null,
      },
      deleted: {
        type: [Array, Object],
        default: null,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      reviewMode: {
        type: Boolean,
        default: false,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      classes: {
        type: Array,
        default: () => [],
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        id: `input-group-checkbox-${uid}`,
        ivalue: this.value,
        ioptions: this.options || [],
        ichanges: [],
        ideleted: [],
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedOptions() {
        const options = this.ioptions || [];
        let values = this.ivalue || [];
        const others = [];
        if (!Array.isArray(values)) {
          values = [];
        }
        values.forEach((v) => {
          if (typeof v === 'string' && v.includes('other-')) {
            others.push({
              value: v,
              label: v.replace('other-', '').replace(/_/g, ' '),
            });
          }
        });

        others.forEach((opt) => {
          const found = options.find(o => o.value === opt.value);
          if (!found) {
            options.push(opt);
          }
        });
        return options;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(val) {
        this.$nextTick(() => {
          this.ivalue = val;
        });
        if (this.reviewMode) {
          this.parseChanges();
        }
      },
      changes() {
        if (this.reviewMode) {
          this.parseChanges();
        }
      },
      deleted() {
        if (this.reviewMode) {
          this.parseChanges();
        }
      },
      ivalue(val) {
        if (!this.reviewMode) {
          const unique = this.forceUnique;
          if (unique !== null && Array.isArray(val) && val.length > 1 && val.includes(unique)) {
            // eslint-disable-next-line no-param-reassign
            val = [unique];
          }
          this.$emit('update:value', val);
          this.$emit('input', val);
        }
      },
      reviewMode(bool) {
        if (bool === false) {
          this.ideleted = [];
          this.ichanges = [];
        }
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      if (this.reviewMode) {
        this.parseChanges();
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      parseChanges() {
        if (this.changes || this.deleted) {
          const changes = JSON.parse(JSON.stringify(this.changes || []));
          const deleted = JSON.parse(JSON.stringify(this.deleted || []));
          const ivalue = JSON.parse(JSON.stringify([...this.ivalue]));
          const ichanges = (Array.isArray(changes) ? changes : Object.values(changes)) || [];
          const ideleted = (Array.isArray(deleted) ? deleted : Object.values(deleted)) || [];

          ichanges.forEach((value) => {
            ivalue.push(value);
          });

          ideleted.forEach((value) => {
            const index = ivalue.findIndex(v => v === value);
            if (index !== -1) {
              ivalue.splice(index, 1);
            }
          });

          this.$nextTick(() => {
            this.ideleted = ideleted;
            this.ichanges = ichanges;
            this.ivalue = ivalue;
          });
        }
      },
      showAddValueModal() {
        const modal = VueUtil.createComponent(ModalAddValue, document.body, {
          propsData: {
            title: this.modal.title,
            description: this.modal.description,
          },
          parent: this,
        });
        modal.$once('add', async (v) => {
          const value = `other-${v.replace(/ /g, '_')}`;
          this.ioptions.push({
            value,
            label: v,
          });
          this.ivalue.push(value);
        });
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleAdopt(accept) {
        this.$emit('adopt', { accept, value: this.ivalue });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-group-checkbox {
  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    & > svg {
      position: relative;
      top: 2px;
      width: 14px;
      height: 14px;
      margin-left: 4px;
    }

    & > .comp-tooltip {
      margin-left: 8px;
    }
  }
  & > div {
    padding: 8px;
    border: 1px solid $color-black-solid-05;
    & > div {
      display: block;
      margin-bottom: 12px;
      &:last-of-type {
        margin-bottom: 0;
      }
    }

    & > button.add {
      margin-top: 6px;
      margin-left: -4px;
      padding: 2px 6px;
      border: 1px solid transparent;
      border-radius: $default-border-radius;
      background: transparent;
      @include font-body-medium;
      color: $color-black-alpha-80;
      cursor: pointer;

      & > span {
        font-weight: 800;
        font-size: 1.6rem;
        margin: 0 6px 0 0;
      }

      &:hover {
        background-color: $color-white-solid-10;
        border: 1px solid $color-blue-solid-100;
        color: $color-blue-solid-100;
      }
    }
  }


  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 8px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  /* -------------------------------
  Additional Styles
  ------------------------------- */
  &.three-columns {
    & > div { column-count: 3; }
  }
  &.two-columns {
    & > div { column-count: 2; }
  }
  &.flex-thirds {
    & > div {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      flex-wrap: wrap;
      & > div {
        width: 33.33%;
      }
    }
  }
  &.flex-thirds {
    & > div {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      flex-wrap: wrap;
      & > div {
        width: 50%;
      }
    }
  }

  &.noborder {
    & > div {
      border: 0;
    }
  }
  &.noicons {
    & > label {
      & > svg {
        display: none;
      }
    }
  }
}
</style>
